import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { fmtRM, scoreColor, STAGE_LABELS, STAGE_COLORS, CHANNEL_META } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, Flame, Phone, Mail, Building2, Tag, MessageSquare, DollarSign, TrendingUp,
  Clock, PlusCircle, Radio, UserCheck, ShieldCheck
} from "lucide-react";

const TIMELINE_ICON = { plus: PlusCircle, source: Radio, chat: MessageSquare, clock: Clock };

export default function CustomerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["contact-profile", id], queryFn: () => api.get(`/contacts/${id}/profile`).then((r) => r.data) });

  if (isLoading || !data) return <div className="p-6"><div className="h-8 w-40 bg-secondary rounded animate-pulse" /></div>;
  const c = data.contact;
  const optedIn = c.opt_in && !c.opt_out;

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto" data-testid="customer-profile-page">
      <button onClick={() => navigate("/app/crm")} data-testid="customer-profile-back" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Kembali ke CRM
      </button>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left: profile card */}
        <div className="rounded-2xl border border-border bg-card p-6 h-fit">
          <div className="text-center">
            <div className="h-20 w-20 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-2xl mx-auto">{c.name[0]}</div>
            <h1 className="font-display text-xl font-bold mt-3" data-testid="customer-profile-name">{c.name}</h1>
            <div className="mt-2 flex justify-center">
              <Badge className={`${scoreColor(c.lead_score)} border-0 rounded-full`}><Flame className="h-3.5 w-3.5 mr-1" /> {c.tier} · {c.lead_score}/100</Badge>
            </div>
          </div>
          <div className="mt-5 space-y-2.5 text-sm">
            <div className="flex items-center gap-2 text-muted-foreground"><Phone className="h-4 w-4" /> {c.phone}</div>
            {c.email && <div className="flex items-center gap-2 text-muted-foreground"><Mail className="h-4 w-4" /> {c.email}</div>}
            {c.company && <div className="flex items-center gap-2 text-muted-foreground"><Building2 className="h-4 w-4" /> {c.company}</div>}
            <div className="flex items-center gap-2 text-muted-foreground"><Radio className="h-4 w-4" /> {c.source}{c.campaign ? ` · ${c.campaign}` : ""}</div>
            <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-muted-foreground" /> Opt-in: {optedIn ? <span className="text-emerald-600 font-medium">YES</span> : <span className="text-rose-600 font-medium">NO</span>}</div>
          </div>
          <div className="mt-4 pt-4 border-t border-border">
            <Badge className={`${STAGE_COLORS[c.stage]} border-0 rounded`}>{STAGE_LABELS[c.stage]}</Badge>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {c.tags?.map((t) => <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground flex items-center gap-0.5"><Tag className="h-2.5 w-2.5" />{t}</span>)}
            </div>
          </div>
          {data.staff && (
            <div className="mt-4 pt-4 border-t border-border flex items-center gap-2 text-sm">
              <UserCheck className="h-4 w-4 text-primary" /> Assigned: <span className="font-medium">{data.staff.name}</span>
            </div>
          )}
        </div>

        {/* Right: stats + timeline + conversations */}
        <div className="lg:col-span-2 space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[[MessageSquare, "Conversations", data.stats.conversations], [MessageSquare, "Messages", data.stats.messages],
              [DollarSign, "Revenue", fmtRM(data.stats.revenue)], [TrendingUp, "Lead Score", data.stats.lead_score]].map(([Icon, l, v], i) => (
              <div key={i} className="rounded-2xl border border-border bg-card p-4" data-testid={`profile-stat-${i}`}>
                <Icon className="h-4 w-4 text-primary" />
                <p className="mt-2 text-xl font-bold font-display">{v}</p>
                <p className="text-[11px] text-muted-foreground">{l}</p>
              </div>
            ))}
          </div>

          <div className="rounded-2xl border border-border bg-card p-6">
            <h2 className="font-display font-semibold mb-4">Timeline</h2>
            <div className="space-y-4">
              {data.timeline.filter((t) => t.date).map((t, i) => {
                const Icon = TIMELINE_ICON[t.icon] || Clock;
                return (
                  <div key={i} className="flex items-start gap-3">
                    <div className="h-8 w-8 rounded-full bg-accent text-primary flex items-center justify-center shrink-0"><Icon className="h-4 w-4" /></div>
                    <div>
                      <p className="text-sm font-medium">{t.label}</p>
                      <p className="text-xs text-muted-foreground">{new Date(t.date).toLocaleString("en-MY")}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-card p-6">
            <h2 className="font-display font-semibold mb-4">Perbualan ({data.conversations.length})</h2>
            {data.conversations.length === 0 && <p className="text-sm text-muted-foreground">Tiada perbualan lagi.</p>}
            <div className="space-y-3">
              {data.conversations.map((conv) => {
                const cm = CHANNEL_META[conv.channel || "whatsapp"] || CHANNEL_META.whatsapp;
                return (
                  <div key={conv.id} className="rounded-xl border border-border p-3">
                    <div className="flex items-center justify-between mb-2">
                      <Badge className={`${cm.cls} border-0 rounded-full text-[10px] capitalize`}>{conv.channel || "whatsapp"}</Badge>
                      <span className="text-xs text-muted-foreground">{conv.messages?.length || 0} mesej</span>
                    </div>
                    <p className="text-sm text-muted-foreground truncate">{conv.last_message}</p>
                    <Button size="sm" variant="ghost" onClick={() => navigate("/app/inbox")} className="mt-1 h-7 text-xs text-primary px-0">Buka di Inbox →</Button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
